import React, { FC, useEffect, useState } from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { translate } from '@utils/locale';
import { yupResolver } from '@hookform/resolvers/yup';
import { CustomModal, FormDualButton, RhfTextField } from '@components';
import { SectionMapper } from '@modules/companies/create-company/constants';
import { useUpdatePreIPOCompanyTermsMutation } from '@modules/companies/create-company/api';
import { showGenericErrorToast } from '@utils/error';
import { API_RESPONSE_STATUS } from '@constants/common';
import { ToastTypes } from '@type/toast';
import displayToast from '@components/toast/customToast';

import {
  CompanyTerms,
  CreatePreIPODealsCompanyFormFieldValues,
  PreIPODealsCompanySectionProps
} from '../types';
import { CreatePreIPODealsCompanyFormDefaultFieldValues } from '../constants';
import { preIPOCompanyDetailsSchema } from '../validate';

const PreIPODealsCompanySection: FC<PreIPODealsCompanySectionProps> = ({
  disableEdit = false,
  editMode,
  updateEditMode,
  getResetFunction,
  companyDetails
}) => {
  const [isCancelClicked, setIsCancelClicked] = useState<boolean>(true);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<
    'discard' | 'save' | undefined
  >();

  const methods = useForm<CreatePreIPODealsCompanyFormFieldValues>({
    resolver: yupResolver(
      preIPOCompanyDetailsSchema
    ) as never as Resolver<CreatePreIPODealsCompanyFormFieldValues>,
    defaultValues: CreatePreIPODealsCompanyFormDefaultFieldValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const buildCompanyTermsFromForm = (
    values: CreatePreIPODealsCompanyFormFieldValues,
    existingTerms?: CompanyTerms // optional, if updating
  ) => {
    const keyMap = {
      peRatio: 'P/E RATIO',
      pbRatio: 'P/B RATIO',
      marketCap: 'MARKET CAP',
      faceValue: 'FACE VALUE',
      bookValue: 'BOOK VALUE'
    };

    const entries = Object.entries(values) as [
      keyof CreatePreIPODealsCompanyFormFieldValues,
      string
    ][];

    const terms = entries
      .map(([key, value], index) => {
        const snakeKey = keyMap[key as keyof typeof keyMap];
        const existingId =
          existingTerms?.find(t => t.term_key === snakeKey)?.id || '';

        return {
          id: existingId,
          term_key: snakeKey,
          term_value: value,
          sequence: index + 1
        };
      })
      .filter(term => term.term_value && term.term_value.trim() !== '');

    return terms;
  };
  const [updateCompanyTerms, { isLoading: isUpdateCompanyTermsLoading }] =
    useUpdatePreIPOCompanyTermsMutation();

  const {
    trigger,
    control,
    formState: { errors },
    clearErrors,
    getValues,
    reset
  } = methods;

  const isEditMode = editMode === SectionMapper.preIpoDetails;
  const onClickCancelButton = () => {
    setIsCancelClicked(true);
    setConfirmModalOpen('discard');
  };
  const onEditSaveButtonClick = async () => {
    if (!editMode) {
      updateEditMode?.(SectionMapper.preIpoDetails);
      return;
    }
    if (editMode !== SectionMapper.preIpoDetails) {
      setConfirmModalOpen('discard');
      return;
    }
    const isFormValid = await trigger();
    if (isFormValid) {
      setConfirmModalOpen('save');
    }
  };

  const { companyId } = useParams();

  const onClickTextField = (
    fieldName: keyof CreatePreIPODealsCompanyFormFieldValues
  ) => {
    clearErrors(fieldName);
  };
  const onDiscardDetails = () => {
    const values = getValues();
    reset(getResetFunction(editMode)(values));
    isCancelClicked
      ? updateEditMode?.()
      : updateEditMode?.(SectionMapper.legalInfo);
    setConfirmModalOpen(undefined);
  };

  const onUpdateCompanyTermsDetails = () => {
    const companyTermsData = buildCompanyTermsFromForm(methods.getValues());

    onCreateCompanyTerms(companyTermsData);
  };

  const onCreateCompanyTerms = async (termsData: CompanyTerms) => {
    if (!termsData) return;
    try {
      const response = await updateCompanyTerms({
        company_id: companyId ?? '',
        payload: {
          terms: termsData
        }
      }).unwrap();

      if (response.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Company Terms Added Successfully');
        updateEditMode?.();
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to add Company Terms'
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
    setConfirmModalOpen(undefined);
  };

  const extractFormValuesFromTerms = (
    terms: {
      id: string;
      term_key: string;
      term_value: string;
      sequence: string | number;
    }[]
  ): Partial<CreatePreIPODealsCompanyFormFieldValues> => {
    const result: Partial<CreatePreIPODealsCompanyFormFieldValues> = {};

    terms.forEach(term => {
      switch (term.term_key) {
        case 'P/E RATIO':
          result.peRatio = term.term_value;
          break;
        case 'P/B RATIO':
          result.pbRatio = term.term_value;
          break;
        case 'MARKET CAP':
          result.marketCap = term.term_value;
          break;
        case 'FACE VALUE':
          result.faceValue = term.term_value;
          break;
        case 'BOOK VALUE':
          result.bookValue = term.term_value;
          break;
        default:
          break;
      }
    });

    return result;
  };

  useEffect(() => {
    if (companyDetails?.terms) {
      const values = extractFormValuesFromTerms(companyDetails.terms);
      reset(values); // from useForm
    }
  }, [companyDetails?.terms]);

  return (
    <div className="pt-9">
      <FormProvider {...methods}>
        <form>
          <div className="flex flex-row justify-between items-center">
            <div className="text-xl font-semibold">
              {translate(
                'preIpoDeals.createPreIPODeals.companyDetails.preIPODealDetails'
              )}
            </div>
            <FormDualButton
              enableEdit={!disableEdit}
              showCancelOrSaveButton={isEditMode}
              onClickCancelButton={onClickCancelButton}
              onClickEditButton={onEditSaveButtonClick}
            />
          </div>
          <div className="w-full lg:w-[70%]">
            <div className="flex flex-row mr-6">
              <RhfTextField
                name="peRatio"
                label={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.peRatio'
                )}
                placeholder={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.peRatioPlaceholder'
                )}
                control={control}
                errors={errors}
                wrapperClass="pt-4 mr-6 w-full"
                onClickTextField={() => onClickTextField('peRatio')}
                isDisabled={!isEditMode}
              />
              <RhfTextField
                name="pbRatio"
                label={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.pbRatio'
                )}
                placeholder={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.pbRatioPlaceholder'
                )}
                control={control}
                errors={errors}
                wrapperClass="pt-4 mr-6 w-full"
                onClickTextField={() => onClickTextField('pbRatio')}
                isDisabled={!isEditMode}
              />
            </div>
            <div className="flex flex-row mr-6">
              <RhfTextField
                name="faceValue"
                label={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.faceValue'
                )}
                placeholder={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.faceValue'
                )}
                control={control}
                errors={errors}
                wrapperClass="pt-4 mr-6 w-full"
                onClickTextField={() => onClickTextField('faceValue')}
                isDisabled={!isEditMode}
              />
              <RhfTextField
                name="bookValue"
                label={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.bookValue'
                )}
                placeholder={translate(
                  'preIpoDeals.createPreIPODeals.companyDetails.bookValue'
                )}
                control={control}
                errors={errors}
                wrapperClass="pt-4 mr-6 w-full"
                onClickTextField={() => onClickTextField('bookValue')}
                isDisabled={!isEditMode}
              />
            </div>
            <div className="flex flex-row mr-6">
              <div className="flex-1 pt-4 mr-6 w-full">
                <RhfTextField
                  name="marketCap"
                  label={translate(
                    'preIpoDeals.createPreIPODeals.companyDetails.marketCap'
                  )}
                  placeholder={translate(
                    'preIpoDeals.createPreIPODeals.companyDetails.marketCap'
                  )}
                  control={control}
                  errors={errors}
                  wrapperClass=""
                  onClickTextField={() => onClickTextField('marketCap')}
                  isDisabled={!isEditMode}
                />
              </div>
              <div className="flex-1 pt-4 mr-6 w-full"></div>
            </div>
          </div>
        </form>
      </FormProvider>
      {isConfirmModalOpen && (
        <CustomModal
          isModalOpen={!!isConfirmModalOpen}
          header={
            isConfirmModalOpen === 'discard'
              ? translate('modals.discardPopupTitle')
              : translate('modals.saveInfoTitle')
          }
          primaryButtonLabel={
            isConfirmModalOpen === 'discard'
              ? translate('modals.confirm')
              : translate('update')
          }
          secondaryButtonVariant="text"
          secondaryButtonLabel={translate('modals.cancelButtonTitle')}
          onChangePrimaryButton={
            isConfirmModalOpen === 'discard'
              ? onDiscardDetails
              : onUpdateCompanyTermsDetails
          }
          onChangeSecondaryButton={() => setConfirmModalOpen(undefined)}
          isPrimaryButtonLoading={isUpdateCompanyTermsLoading}
          isPrimaryButtonDisabled={isUpdateCompanyTermsLoading}
        />
      )}
    </div>
  );
};
export default PreIPODealsCompanySection;
